import React, { FC, memo, useEffect } from 'react';

import { TEXTS } from 'application/pages/texts';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { RouterUtils, useLocation, useNavigate } from 'application/providers/RouterProvider';
import { LocationStatePointsType } from 'application/pages/types';
import { POINTS_MENU_PATH, STREAMS_CATEGORY_PATH } from 'application/consts';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';

import { PointAddressesList, ModalTitle, ModalDescription, SuccessActionButton } from './components';
import { BREAK_EDGE_VALUE } from './consts';
import useStreamBindSuccessModalViewModel from './useStreamBindSuccessModalViewModel';
import { useOnboardingContext } from '../useOnboardingContext';
import { useOnboardingActionContext } from '../useOnboardingActionContext';

import styles from './index.module.scss';
import { SelectPlayerFlowType } from '../types';

const StreamBindSuccessModal: FC<ModalProps[ModalTypes.STREAM_BIND_SUCCESS]> = ({
  close,
  selectedPoints: selectedPointsProps = [],
  successCallback,
  replaceModal,
}) => {
  const { flowType } = useOnboardingContext();
  const { resetState } = useOnboardingActionContext();

  const navigate = useNavigate();
  const location = useLocation<LocationStatePointsType>();
  const [pointFromLocation] = location.state?.pointsFromLocation || [];

  useEffect(
    () => () => {
      if (flowType === SelectPlayerFlowType.BIND_STREAM) {
        resetState();
      }
    },
    []
  );

  const {
    selectedPoints,
    currentPoint,
    isNeedPayment,
    isPointPage,
    showStreamsButton,
    showWebPlayerButton,
    showExpandButton,
    isChangeStreamFlow,
    isSettingsFinished,
    isBillingEnabled,
    onBindPointSuccessClick,
    onChangeStreamClick,
    onClose,
    setIsAllPointsVisible,
  } = useStreamBindSuccessModalViewModel({
    flowType,
    selectedPointsProps,
    pointId: pointFromLocation?.pointId,
    pathname: location.pathname,
    redirectToPointsPage: () => {
      navigate(RouterUtils.generatePath(POINTS_MENU_PATH));
    },
    redirectToStreamsPage: () => {
      navigate(
        RouterUtils.generatePath(STREAMS_CATEGORY_PATH, {
          category: 'recommended',
        }),
        {
          state: { pointsFromLocation: [{ pointId: currentPoint?.id, pointAddress: currentPoint?.address }] },
        }
      );
    },
    close,
    successCallback,
    replaceModal,
  });

  return (
    <ModalScreen
      title={
        <ModalTitle
          isPointPage={isPointPage}
          isNeedPayment={isNeedPayment}
        />
      }
      titlePlacement="left"
      containerClassName={styles.container}
      onClose={onClose}
      actionButtons={[
        <SuccessActionButton
          key="button_open_whatever_bind_success_modal"
          showStreamsButton={showStreamsButton}
          showWebPlayerButton={showWebPlayerButton}
          isChangeStreamFlow={isChangeStreamFlow}
          close={close}
          onChangeStreamClick={onChangeStreamClick}
          onBindPointSuccessClick={onBindPointSuccessClick}
        />,
        !isPointPage && (
          <Button
            key="button_cancel_bind_success_modal"
            elementId="button_cancel_bind_success_modal"
            type={ButtonType.SECONDARY}
            onClick={onClose}>
            {TEXTS.MODAL_BIND_SUCCESS_BUTTON_CANCEL_TEXT}
          </Button>
        ),
      ]}>
      <ModalDescription
        flowType={flowType}
        isSettingsFinished={isSettingsFinished}
        isNeedPayment={isNeedPayment}
        isAddressWithStreamDisabled={!currentPoint?.isEnabled && isBillingEnabled && Boolean(currentPoint?.stream)}
      />

      {selectedPoints.length > 0 && (
        <div className={styles.pointList}>
          <PointAddressesList
            selectedPoints={showExpandButton ? selectedPoints.slice(0, BREAK_EDGE_VALUE) : selectedPoints}
          />

          {showExpandButton && (
            <Button
              elementId="stream_bind_success_modal_button_show_all"
              className={styles.button}
              type={ButtonType.PLAIN}
              onClick={() => setIsAllPointsVisible(true)}>
              {TEXTS.MODAL_BIND_SUCCESS_MORE_ADDRESS(selectedPoints.length - BREAK_EDGE_VALUE)}
            </Button>
          )}
        </div>
      )}
    </ModalScreen>
  );
};

export default memo(StreamBindSuccessModal);
